<script>
  import { createEventDispatcher } from "svelte";
  import { formatDur, spanColor } from "./utils.js";

  export let traces = [];
  export let selectedTrace = null;
  export let selectedSpan = null;
  export let waterfallTimeRange = { minNs: 0, maxNs: 0, durationNs: 1 };
  export let waterfallTicks = [];
  export let sortedWaterfallSpans = [];
  export let spanDepths = new Map();
  export let colorBy = "name";

  export let WATERFALL_LABEL_W = 300;

  const dispatch = createEventDispatcher();
</script>

<div class="flex-1 min-h-0 flex">
  <!-- Traces list -->
  <div class="w-64 shrink-0 border-r flex flex-col">
    <div
      class="px-3 py-2 text-xs font-semibold text-content-subtle uppercase tracking-wide border-b bg-surface-sunken"
    >
      Traces ({traces.length})
    </div>
    <div class="flex-1 min-h-0 overflow-y-auto">
      {#if traces.length === 0}
        <div class="p-4 text-center text-sm text-content-subtle">No traces</div>
      {:else}
        {#each traces as trace}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div
            class="px-3 py-2 cursor-pointer border-b border-line-subtle hover:bg-surface-hover {selectedTrace?.trace_id ===
            trace.trace_id
              ? 'bg-blue-50 border-l-2 border-l-blue-500'
              : ''}"
            on:click={() => dispatch("selectTrace", { trace })}
          >
            <div class="text-xs font-medium text-content truncate">
              {trace.root_span_name}
            </div>
            <div class="text-[10px] text-content-subtle mt-0.5">
              {trace.span_count} spans · {formatDur(trace.duration_ns)}
            </div>
          </div>
        {/each}
      {/if}
    </div>
  </div>

  <!-- Waterfall chart -->
  <div class="flex-1 min-w-0 flex flex-col">
    {#if selectedTrace}
      <!-- Trace ID header -->
      <div
        class="px-3 py-1.5 text-[10px] text-content-subtle font-mono border-b bg-surface-sunken truncate shrink-0"
      >
        {selectedTrace.trace_id}
      </div>

      <!-- Column headers with time axis -->
      <div
        class="shrink-0 flex border-b border-line-strong bg-surface-sunken text-[10px] text-content-subtle select-none"
      >
        <div
          class="shrink-0 border-r border-line-strong px-2 flex items-end pb-1"
          style="width:{WATERFALL_LABEL_W}px;"
        >
          Span
        </div>
        <div class="w-16 shrink-0 border-r border-line-strong flex items-end justify-end px-2 pb-1">
          Duration
        </div>
        <!-- Time axis -->
        <div class="flex-1 relative" style="height:26px;">
          {#each waterfallTicks as tick}
            <span
              class="absolute bottom-1 text-[10px] text-content-subtle"
              style="left:{tick.pct}%;transform:translateX(-50%);"
            >
              {tick.label}
            </span>
            <span
              class="absolute bottom-0 w-px bg-surface-hover"
              style="left:{tick.pct}%;height:5px;"
            />
          {/each}
        </div>
      </div>

      <!-- Span rows -->
      <div class="flex-1 min-h-0 overflow-y-auto">
        {#each sortedWaterfallSpans as span}
          {@const depth = spanDepths.get(span.span_id) ?? 0}
          {@const durNs = span.end_time_ns - span.start_time_ns}
          {@const barLeft =
            waterfallTimeRange.durationNs > 0
              ? ((span.start_time_ns - waterfallTimeRange.minNs) / waterfallTimeRange.durationNs) *
                100
              : 0}
          {@const barWidth =
            waterfallTimeRange.durationNs > 0
              ? Math.max(0.5, (durNs / waterfallTimeRange.durationNs) * 100)
              : 0.5}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div
            class="flex items-center border-b border-line-subtle hover:bg-surface-hover cursor-pointer h-7 {selectedSpan?.span_id ===
            span.span_id
              ? 'bg-blue-50'
              : ''}"
            on:click={() => dispatch("selectSpan", { span })}
          >
            <!-- Name with indent -->
            <div
              class="shrink-0 flex items-center min-w-0 border-r border-line-subtle h-full"
              style="width:{WATERFALL_LABEL_W}px;padding-left:{8 + depth * 14}px;"
            >
              {#if depth > 0}
                <span class="text-gray-300 mr-1 shrink-0">└</span>
              {/if}
              <span
                class="text-xs text-content truncate"
                class:font-semibold={!span.parent_span_id}
                title={span.name}
              >
                {span.name}
              </span>
            </div>
            <!-- Duration -->
            <div
              class="w-16 shrink-0 text-[10px] text-content-subtle tabular-nums text-right px-2 border-r border-line-subtle h-full flex items-center justify-end"
            >
              {formatDur(durNs)}
            </div>
            <!-- Bar -->
            <div class="flex-1 relative h-full px-1">
              <div
                class="absolute top-1 h-5 rounded-sm"
                style="left:{barLeft}%;width:{barWidth}%;background-color:{spanColor(span, colorBy)
                  .bg};border:1px solid {spanColor(span, colorBy).border};"
              ></div>
              <!-- Grid lines -->
              {#each waterfallTicks as tick}
                <span
                  class="absolute top-0 bottom-0 w-px bg-surface-muted"
                  style="left:{tick.pct}%;"
                />
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <div class="flex-1 flex items-center justify-center text-sm text-content-subtle">
        Select a trace
      </div>
    {/if}
  </div>
</div>
